import { observer } from "mobx-react";
import { useId, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/panel";
import { useTranslation } from "@/i18n";

interface TeammateRowProps {
  /** The teammate's display name, e.g. "Reporting Teammate". */
  name: string;
  /** What it may do, in one plain sentence (the definition's consent summary). */
  summary: string;
  /** A status badge beside the name, if any. */
  status?: ReactNode;
  /** The row's control: the admin's switch, the member's Turn on / Turn off. */
  control?: ReactNode;
  /** A standing note under the summary (needs approval, waiting for an admin). */
  notice?: ReactNode;
  /** Opens the full permission grid. */
  onViewPermissions: () => void;
  /** A write for this teammate is still out; announced as `aria-busy`. */
  busy?: boolean;
}

/**
 * One digital teammate on a settings screen (NG-2622): name, summary, status,
 * its control and "View full permissions". Layout only, shared by the admin's
 * and the member's screen so the two read as the same thing. A named region,
 * so assistive tech (and the e2e suite) can find a teammate by its name.
 */
export const TeammateRow = observer(function TeammateRow({
  name,
  summary,
  status,
  control,
  notice,
  onViewPermissions,
  busy = false,
}: TeammateRowProps) {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <Panel
      role="region"
      aria-labelledby={headingId}
      aria-busy={busy || undefined}
      className="space-y-3"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id={headingId} className="text-sm font-semibold">
              {name}
            </h3>
            {status}
          </div>
          <p className="text-sm text-muted-foreground">{summary}</p>
        </div>
        {control ? <div className="flex shrink-0 items-center gap-2">{control}</div> : null}
      </div>
      {notice}
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto px-0"
        onClick={onViewPermissions}
      >
        {t("digital_teammates.view_permissions")}
      </Button>
    </Panel>
  );
});
